import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent } from 'react';

import { m } from 'motion/react';
import { ArrowDown, ArrowUp, ArrowUpDown, FolderGit2, Plus, Search, X } from 'lucide-react';

import { projects, type IntegrationState, type Project, type ProjectListItem } from '../../api/projects';
import { Badge } from '../../components/Badge';
import type { BadgeTone } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { useFormat } from '../../i18n/format';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { slugify } from '../../lib/slug';
import { useSubmit } from '../auth/useSubmit';
import { useCompanyId, useCompanySlug } from '../company/useCompany';
import { DEFAULT_SORT, NO_FILTERS, STATUSES, matches, sortProjects } from './projectFilters';
import type { Filters, IntegrationFilter, Sort, SortKey, StatusFilter } from './projectFilters';

/** Solo el tono: la palabra sale de `projects.status.*`. */
const STATUS_TONE: Record<Project['status'], BadgeTone> = {
  planning: 'info',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};

/** Solo el tono: la palabra sale de `projects.integration.*`. */
const INTEGRATION_TONE: Record<IntegrationState, BadgeTone> = {
  configured: 'success',
  not_configured: 'neutral',
  no_connection: 'warning',
};

const INTEGRATION_STATES = ['configured', 'not_configured', 'no_connection'] as const;

/**
 * `13 §2`: los proyectos de una empresa.
 *
 * Un proyecto es la unidad que posee repositorios, así que esta pantalla es la
 * entrada a todo lo que añadió la fase 5. El slug es prominente y no
 * decorativo: es el `{project}` de la plantilla de nombres de repositorio
 * (`08 §3.1`), lo que significa que pasa a formar parte de un nombre permanente
 * en el servidor de otro.
 *
 * Es una tabla y no una lista porque aquí se comparan proyectos: cuándo se
 * pidió trabajo por última vez, cuándo terminó algo, y si Kubernetes y Jenkins
 * están montados. Esos datos llegan en el `summary` de cada fila; los filtros
 * y el orden son de cliente, porque una empresa tiene un puñado de proyectos y
 * la lista ya está entera en memoria.
 */
export function ProjectsScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const companySlug = useCompanySlug();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);

  // Pedir «archivados» como estado tiene que enseñar archivados aunque la
  // casilla esté apagada: si no, el filtro devolvería siempre una tabla vacía.
  const withArchived = includeArchived || filters.status === 'archived';

  const list = useQuery({
    queryKey: ['projects', companyId, withArchived],
    queryFn: () => projects.list(companyId, withArchived),
    enabled: companyId !== '',
  });

  const rows = useMemo(() => {
    const all = list.data?.data ?? [];

    return sortProjects(all.filter((project) => matches(project, filters)), sort);
  }, [list.data, filters, sort]);

  const filtering = filters.query !== '' || filters.status !== '' || filters.kubernetes !== '' || filters.jenkins !== '';
  const total = list.data?.data.length ?? 0;

  const toggleSort = (key: SortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'name' || key === 'status' ? 'asc' : 'desc' },
    );

  return (
    <section className="space-y-6">
      <PageHeader
        title={t('projects.title')}
        description={t('projects.intro')}
        actions={
          <Button icon={creating ? undefined : <Plus />} onClick={() => setCreating((open) => !open)}>
            {creating ? t('common.cancel') : t('projects.newProject')}
          </Button>
        }
      />

      {creating ? (
        <CreateProjectForm
          companyId={companyId}
          onCreated={async () => {
            setCreating(false);
            await queryClient.invalidateQueries({ queryKey: ['projects', companyId] });
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <SearchBox value={filters.query} onChange={(query) => setFilters((current) => ({ ...current, query }))} />

        <FilterSelect
          label={t('projects.filters.status')}
          value={filters.status}
          onChange={(status) => setFilters((current) => ({ ...current, status: status as StatusFilter }))}
          any={t('projects.filters.anyStatus')}
          options={STATUSES.map((status) => [status, t(`projects.status.${status}` as Key)])}
        />

        <FilterSelect
          label={t('projects.filters.kubernetes')}
          value={filters.kubernetes}
          onChange={(kubernetes) => setFilters((current) => ({ ...current, kubernetes: kubernetes as IntegrationFilter }))}
          any={t('projects.filters.anyIntegration')}
          options={INTEGRATION_STATES.map((state) => [state, t(`projects.integration.${state}` as Key)])}
        />

        <FilterSelect
          label={t('projects.filters.jenkins')}
          value={filters.jenkins}
          onChange={(jenkins) => setFilters((current) => ({ ...current, jenkins: jenkins as IntegrationFilter }))}
          any={t('projects.filters.anyIntegration')}
          options={INTEGRATION_STATES.map((state) => [state, t(`projects.integration.${state}` as Key)])}
        />

        <label className="flex min-h-9 cursor-pointer items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={withArchived}
            disabled={filters.status === 'archived'}
            onChange={(event) => setIncludeArchived(event.target.checked)}
            className="size-4 accent-primary"
          />
          {t('projects.showArchived')}
        </label>

        {filtering ? (
          <Button variant="ghost" size="sm" icon={<X />} onClick={() => setFilters(NO_FILTERS)}>
            {t('projects.filters.clear')}
          </Button>
        ) : null}
      </div>

      {list.isPending ? <SkeletonRows /> : null}
      {list.error ? <ErrorBanner error={list.error} onRetry={() => void list.refetch()} /> : null}

      {list.data && total === 0 ? (
        <EmptyState
          icon={<FolderGit2 />}
          title={t('projects.empty')}
          description={t('projects.emptyHint')}
          action={<Button onClick={() => setCreating(true)}>{t('projects.createFirst')}</Button>}
        />
      ) : null}

      {list.data && total > 0 && rows.length === 0 ? (
        <EmptyState
          tone="muted"
          icon={<Search />}
          title={t('projects.filters.noMatch')}
          description={t('projects.filters.noMatchHint')}
          action={
            <Button variant="secondary" onClick={() => setFilters(NO_FILTERS)}>
              {t('projects.filters.clear')}
            </Button>
          }
        />
      ) : null}

      {rows.length > 0 ? (
        <m.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-x-auto rounded-lg border border-line bg-raised"
        >
          <p className="px-4 pt-3 text-xs text-ink-faint">
            {t('projects.filters.showing', { shown: rows.length, total })}
          </p>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs text-ink-faint">
                <SortableHeader column="name" label={t('projects.table.project')} sort={sort} onSort={toggleSort} />
                <SortableHeader column="status" label={t('projects.table.status')} sort={sort} onSort={toggleSort} />
                <th className="px-3 py-2 font-semibold">{t('projects.table.lead')}</th>
                <SortableHeader
                  column="tasks"
                  label={t('projects.table.tasks')}
                  title={t('projects.table.tasksHint')}
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                />
                <SortableHeader column="lastRequested" label={t('projects.table.lastRequested')} sort={sort} onSort={toggleSort} />
                <SortableHeader column="lastFinished" label={t('projects.table.lastFinished')} sort={sort} onSort={toggleSort} />
                <th className="px-3 py-2 text-right font-semibold">{t('projects.table.repositories')}</th>
                <th className="px-3 py-2 text-right font-semibold">{t('projects.table.members')}</th>
                <th className="px-3 py-2 font-semibold">{t('projects.table.kubernetes')}</th>
                <th className="px-3 py-2 font-semibold">{t('projects.table.jenkins')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((project) => (
                <ProjectRow key={project.id} project={project} companySlug={companySlug} />
              ))}
            </tbody>
          </table>
        </m.div>
      ) : null}
    </section>
  );
}

function ProjectRow({ project, companySlug }: { project: ProjectListItem; companySlug: string }) {
  const t = useT();
  const { summary } = project;

  return (
    <tr className="border-b border-line align-top transition-colors last:border-0 hover:bg-hover">
      <td className="min-w-48 px-3 py-2.5">
        <Link
          to="/c/$companySlug/projects/$projectSlug"
          params={{ companySlug, projectSlug: project.slug }}
          className="text-sm font-semibold text-ink transition-colors hover:text-primary"
        >
          {project.name}
        </Link>
        <p className="truncate text-xs text-ink-faint">
          <span className="font-mono">{project.slug}</span>
          {project.tech_stack.length > 0 ? ` · ${project.tech_stack.join(', ')}` : ''}
        </p>
      </td>
      <td className="px-3 py-2.5">
        <Badge tone={STATUS_TONE[project.status]} size="sm">
          {t(`projects.status.${project.status}` as Key)}
        </Badge>
      </td>
      <td className="px-3 py-2.5 text-ink-soft">{summary.lead_name ?? '—'}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-ink" title={t('projects.table.tasksHint')}>
        <span className="font-semibold">{summary.tasks.open}</span>
        <span className="text-ink-faint"> / {summary.tasks.total}</span>
      </td>
      <Moment iso={summary.tasks.last_requested_at} />
      <Moment iso={summary.tasks.last_finished_at} />
      <td className="px-3 py-2.5 text-right tabular-nums text-ink">{summary.repositories}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-ink">{summary.members}</td>
      <IntegrationCell
        tab="kubernetes"
        state={summary.kubernetes.state}
        detail={
          summary.kubernetes.context !== null
            ? `${summary.kubernetes.context} · ${t('projects.integration.namespaces', { count: summary.kubernetes.namespaces.length })}`
            : null
        }
        project={project}
        companySlug={companySlug}
      />
      <IntegrationCell
        tab="jenkins"
        state={summary.jenkins.state}
        detail={summary.jenkins.job_name}
        project={project}
        companySlug={companySlug}
      />
    </tr>
  );
}

/** Relativa y absoluta a la vez: «hace 3 h» dice lo que importa, la fecha lo fija. */
function Moment({ iso }: { iso: string | null }) {
  const t = useT();
  const format = useFormat();

  return iso === null ? (
    <td className="px-3 py-2.5 text-xs text-ink-faint">{t('projects.table.never')}</td>
  ) : (
    <td className="px-3 py-2.5 whitespace-nowrap text-ink-soft" title={format.dateTime(iso)}>
      <time dateTime={iso}>{format.relative(iso)}</time>
      <span className="block text-xs text-ink-faint">{format.date(iso)}</span>
    </td>
  );
}

/**
 * El estado de una integración, enlazado a su pestaña: quien ve «Sin
 * configurar» quiere ir a configurarlo, no a leer el resumen del proyecto.
 */
function IntegrationCell({
  tab,
  state,
  detail,
  project,
  companySlug,
}: {
  tab: 'kubernetes' | 'jenkins';
  state: IntegrationState;
  detail: string | null;
  project: ProjectListItem;
  companySlug: string;
}) {
  const t = useT();

  return (
    <td className="px-3 py-2.5">
      <Link
        to="/c/$companySlug/projects/$projectSlug"
        params={{ companySlug, projectSlug: project.slug }}
        search={{ tab }}
        aria-label={`${t(`projects.integration.${state}` as Key)} · ${t('projects.table.openTab', { tab: t(`projects.tabs.${tab}` as Key) })}`}
        title={state === 'no_connection' ? t('projects.integration.noConnectionHint') : undefined}
        className="inline-block rounded-full transition-opacity hover:opacity-80"
      >
        <Badge tone={INTEGRATION_TONE[state]} size="sm">
          {t(`projects.integration.${state}` as Key)}
        </Badge>
      </Link>
      {detail !== null ? <p className="mt-1 max-w-56 truncate font-mono text-xs text-ink-faint">{detail}</p> : null}
    </td>
  );
}

function SortableHeader({
  column,
  label,
  title,
  sort,
  onSort,
  align = 'left',
}: {
  column: SortKey;
  label: string;
  title?: string;
  sort: Sort;
  onSort: (column: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const t = useT();
  const active = sort.key === column;
  const Icon = !active ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;

  return (
    <th
      className={cn('px-3 py-2 font-semibold', align === 'right' && 'text-right')}
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        title={title ?? t('projects.table.sortBy', { column: label })}
        className={cn(
          'inline-flex items-center gap-1 rounded-xs transition-colors hover:text-ink',
          active && 'text-ink',
          align === 'right' && 'flex-row-reverse',
        )}
      >
        {label}
        <Icon className={cn('size-3', !active && 'opacity-50')} aria-hidden="true" />
      </button>
    </th>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  any,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  any: string;
  options: [string, string][];
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold text-ink-soft">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(fieldStyles, 'w-auto min-w-36 border-line py-1.5 text-sm font-normal')}
      >
        <option value="">{any}</option>
        {options.map(([option, text]) => (
          <option key={option} value={option}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const t = useT();

  return (
    <label className="relative flex items-center self-end">
      <span className="sr-only">{t('projects.filters.search')}</span>
      <Search className="pointer-events-none absolute left-2.5 size-4 text-ink-faint" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('projects.filters.search')}
        title={t('projects.filters.searchHint')}
        className={cn(fieldStyles, 'w-56 border-line py-1.5 pr-8 pl-8 text-sm sm:w-64')}
      />
      {value !== '' ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('projects.filters.clearSearch')}
          className="absolute right-1.5 grid size-6 place-items-center rounded-sm text-ink-faint transition-colors duration-fast hover:bg-muted hover:text-ink"
        >
          <X className="size-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </label>
  );
}

function CreateProjectForm({ companyId, onCreated }: { companyId: string; onCreated: () => Promise<void> }) {
  const t = useT();
  const { busy, error, fields, submit } = useSubmit<Project>();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    void submit(
      () =>
        projects.create(companyId, {
          name,
          slug: effectiveSlug,
          description: String(form.get('description') ?? '') || null,
          goals: String(form.get('goals') ?? '') || null,
          tech_stack: String(form.get('tech_stack') ?? '')
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== ''),
          status: String(form.get('status') ?? 'planning'),
        }),
      () => void onCreated(),
    );
  };

  return (
    <m.form
      onSubmit={onSubmit}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-xl space-y-4 rounded-lg border border-line bg-raised p-5 shadow-sm"
    >
      {error ? <ErrorBanner error={error} /> : null}

      <Field
        label={t('projects.form.name')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={fields.name}
        required
      />

      <Field
        label={t('projects.form.slug')}
        name="slug"
        value={effectiveSlug}
        onChange={(event) => {
          setSlugTouched(true);
          setSlug(event.target.value);
        }}
        error={fields.slug}
        hint={t('projects.form.slugHint')}
        required
      />

      <Field label={t('projects.form.description')} name="description" error={fields.description} />
      <Field label={t('projects.form.goals')} name="goals" error={fields.goals} />
      <Field
        label={t('projects.form.techStack')}
        name="tech_stack"
        error={fields.tech_stack}
        hint={t('projects.form.techStackHint')}
      />

      <div className="space-y-1.5">
        <label htmlFor="project-status" className="block text-sm font-semibold text-ink">
          {t('projects.form.status')}
        </label>
        <select id="project-status" name="status" defaultValue="planning" className={cn(fieldStyles, 'border-line')}>
          <option value="planning">{t('projects.status.planning')}</option>
          <option value="active">{t('projects.status.active')}</option>
          <option value="paused">{t('projects.status.paused')}</option>
        </select>
      </div>

      <Button type="submit" busy={busy}>
        {t('projects.form.submit')}
      </Button>
    </m.form>
  );
}
